import React, { useEffect, useState } from 'react'
import { Modal, Button } from 'react-bootstrap';
import { useAppSelector, useAppDispatch } from '../../../hooks/redux/hooks';
import ChoosePlan from './steps/ChoosePlan';
import PlanSummary from './steps/PlanSummary';
import { planUpgradeState, resetPlanUpgrade } from '../planUpgradeSlice';
import { usePlanForSummary } from '../hook/usePlan';
import SmashSpinner from '../../../components/SmashSpinner/SmashSpinner';
import { resetPlanUsage } from '../../planUsage/planUsageSlice';
import BillingInformationStep from './steps/BillingInformationStep';
import { useStripeContext } from '../../../context/StripeContext';
import { usePlanUsage } from '../../planUsage/hooks/usePlanUsage';
import { PaymentIntentResult } from '@stripe/stripe-js';
import { selectedUser } from '../../auth/authSlice';
import { generateSubscriptionAPIGTMEvent, pushGTMEvent } from '../../../shared/helpers/gtm';
import { paymentMethodState, executeStripePayment, prepareStripePayment, resetPaymentMethod, setUserAction } from '../../paymentMethod/paymentMethodSlice';
import SmashPrice from '../../../components/SmashPrice/SmashPrice';

const PlanUpgradeModal = () => {
  const dispatch = useAppDispatch();
  const [isPlanUsageLoading, planUsageDisplay] = usePlanUsage();
  const [isSummaryLoading, planSummary] = usePlanForSummary();
  const { selectedPlan } = useAppSelector(planUpgradeState);
  const { payment } = useAppSelector(paymentMethodState);
  const { identity } = useAppSelector(selectedUser);
  const { stripe } = useStripeContext();
  const [paymentIntentProcessing, setPaymentIntentProcessing] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [show, setShow] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntentResult | undefined>();
  const [paymentIntentIsLoading, setPaymentIntentIsLoading] = useState(false);

  const resetStates = () => {
    setPaymentIntentIsLoading(false);
    setPaymentIntentProcessing(false);
    setPaymentProcessing(false);
    setPaymentIntent(undefined);
  }

  const handleClose = () => {
    setShow(false);
    resetStates();
    dispatch(resetPlanUpgrade());
    dispatch(resetPlanUsage());
    dispatch(resetPaymentMethod());
  };

  const handleShow = () => {
    setStep(0);
    resetStates();
    setShow(true);
  };

  const handleRetry = () => {
    resetStates();
    dispatch(resetPaymentMethod());
    setStep(2);
  }

  const [step, setStep] = useState(0);
  const previous = () => { setStep(step - 1) }
  const next = () => { setStep(step + 1) }

  const generatePaymentIntent = async () => {
    if (!paymentIntentIsLoading && stripe && payment.secret && payment.token) {
      setPaymentIntentIsLoading(true);
      const stripePaymentIntent = await stripe.confirmCardPayment(payment.secret, { payment_method: { card: { token: payment.token } } });
      setPaymentIntent(stripePaymentIntent);
    }
  }

  const processPayment = () => {
    setPaymentIntentProcessing(true);
    if (payment.token && selectedPlan.plan) {
      dispatch(prepareStripePayment({ cardToken: payment.token, selectedPlan: selectedPlan.plan }));
    }
  }

  useEffect(() => {
    dispatch(setUserAction('purchase'))
  }, []);

  useEffect(() => {
    if (payment.paymentStatus === 'confirmCardPayment' && payment.token && payment.secret) {
      generatePaymentIntent();
    }
  }, [payment.token, payment.secret, payment.paymentStatus]);

  useEffect(() => {
    if (paymentIntent && !paymentProcessing && payment.paymentStatus === 'confirmCardPayment') {
      setPaymentProcessing(true);
      setPaymentIntentProcessing(false);
      dispatch(executeStripePayment(paymentIntent));
    }
  }, [paymentProcessing, paymentIntent, payment.paymentStatus]);

  useEffect(() => {
    if ((paymentProcessing || paymentIntentProcessing) && payment.paymentStatus === 'paymentFailed') {
      setPaymentIntentProcessing(false);
      setPaymentProcessing(false);
    }

    if (payment.paymentStatus === "paymentSucceeded") {
      if (identity && selectedPlan?.plan) {
        const event = generateSubscriptionAPIGTMEvent({
          identity,
          plan: selectedPlan.plan,
        });
        pushGTMEvent(event);
      }
      handleClose();
    }
  }, [paymentProcessing, paymentIntent, payment, identity, selectedPlan]);

  type ModalSizeType = 'sm' | 'lg' | 'xl' | undefined;
  type ButtonType = "button" | "submit" | "reset" | undefined;
  type ModalBackdrop = 'static' | boolean;
  const formSteps = [
    { modalBackdrop: true, component: <ChoosePlan />, title: "Choose your plan", modalSize: 'lg', },
    { modalBackdrop: true, component: <BillingInformationStep informationFormId="information-modal" nextStep={next} />, title: "Billing information", type: "submit", form: "information-modal" },
    { modalBackdrop: 'static', component: <PlanSummary />, title: "Payment information" },
  ];

  const buttonText = planUsageDisplay?.price === 0 ? 'Upgrade to paid plan' : 'Upgrade'; // FIX ME

  return (
    <>
      <Button onClick={handleShow}>{buttonText}</Button>
      <Modal centered show={show} onHide={handleClose} size={formSteps[step].modalSize as ModalSizeType} backdrop={formSteps[step].modalBackdrop as ModalBackdrop}>
        <Modal.Header closeButton>
          <Modal.Title>
            {formSteps[step].title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(paymentProcessing || paymentIntentProcessing) && < SmashSpinner />}
          {(!paymentProcessing && !paymentIntentProcessing) && payment.paymentStatus !== 'paymentFailed' && <div>{formSteps[step].component}</div>}
          {(payment.paymentStatus === 'paymentFailed') && <div>Seems your card or payment provider is having a bad day. Don't worry, no money has been charged.</div>}
        </Modal.Body>
        <Modal.Footer>
          {(payment.paymentStatus === 'stripeTokenKO' && payment.stripeError && <div className='text-danger'>{payment.stripeError}</div>)}
          {paymentProcessing && <>Processing...</>}
          {(payment.paymentStatus === 'paymentFailed') && <><Button variant="light" onClick={handleClose}>Close</Button><Button onClick={handleRetry}>Retry</Button></>}
          {payment.paymentStatus !== 'paymentFailed' && <>
            {(!paymentIntentProcessing && !paymentProcessing) && step > 0 && <Button onClick={previous} variant='light'>Previous</Button>}
            {(!paymentIntentProcessing && !paymentProcessing) && step < formSteps.length - 1 && !formSteps[step].type && <Button disabled={!selectedPlan.plan} onClick={next}>Next</Button>}
            {(!paymentIntentProcessing && !paymentProcessing) && step < formSteps.length - 1 && formSteps[step].type && <Button type={formSteps[step].type as ButtonType} form={formSteps[step].form}>Next</Button>}
            {(!paymentIntentProcessing && !paymentProcessing) && step === formSteps.length - 1 && planSummary &&
              <Button disabled={payment.paymentStatus !== 'stripeTokenOK'} onClick={processPayment}>
                {['generatingStripeToken', ''].indexOf(payment.paymentStatus) > -1 && <SmashSpinner variant="light" size="sm" />}
                {['generatingStripeToken'].indexOf(payment.paymentStatus) === -1 && <>Pay (<SmashPrice price={planSummary.priceInclTax} currency={planSummary.currency} />)</>}
              </Button>}
          </>}
        </Modal.Footer>
      </Modal>
    </>

  )
}

export default PlanUpgradeModal